import { useCliftContext } from "../../hooks/useCliftContext";
import { CliftReducerAction } from "../../context/clift-context/clift-action-types";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SideModal from "../../components/SideModal";
import { editEmployee, fetchEmployee } from "../../services/employees-api";
import {
  getErrorCode,
  getLocalizedErrorReason,
} from "../../services/clift-api-errors";
import { RoleFormValues } from "models/role";
import { EditEmployeeForm } from "./EditEmployeeForm";
import { EmployeeResponseFull } from "models/user";
import { SideModalButtonBarProps } from "../../components/SideModalButtonBar";
import { toast } from "react-toast";

export interface EditEmployeeProps {
  userId: number;
  onEditCompleted?: () => void;
}

export const EditEmployee = ({
  userId,
  onEditCompleted,
}: EditEmployeeProps) => {
  const { cliftState, dispatchCliftState } = useCliftContext();
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();
  const [employee, setEmployee] = useState<EmployeeResponseFull>();
  const [bottomButtonsProps, setBottomButtonsProps] =
    useState<SideModalButtonBarProps>();

  useEffect(() => {
    fetchEmployee(cliftState.currentTenant!.id, userId)
      .then((res) => {
        setEmployee(res);
      })
      .catch((err) => {
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("data_fetch_failed", {
            ns: "alerts",
            code: getErrorCode(err),
          }),
        });
      });
  }, []);

  if (loading) return <progress></progress>;

  const closeSidePanel = () => {
    dispatchCliftState({
      type: CliftReducerAction.CloseSidePanel,
    });
  };

  const onEditEmployees = (roles: RoleFormValues[]) => {
    if (!cliftState.currentTenant) throw new Error("No current tenant");

    setLoading(true);

    return editEmployee(cliftState.currentTenant.id, userId, roles)
      .then(() => {
        onEditCompleted?.();
        setLoading(false);
        closeSidePanel();
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("user_edit_success", {
            ns: "alerts",
            userId: userId,
          }),
        });
        toast.success(t("changes_saved", { ns: "toasts" }));
      })
      .catch((err: Error) => {
        setLoading(false);
        dispatchCliftState({
          type: CliftReducerAction.AddAlert,
          alert: t("user_edit_failed", {
            ns: "alerts",
            userId: userId,
            code: getErrorCode(err),
          }),
        });
        toast.error(
          t("error") +
            " " +
            t("edit_failed", {
              ns: "toasts",
              reason: getLocalizedErrorReason(err),
            })
        );
      });
  };
  if (!employee) return <progress />;

  return (
    <SideModal
      title={t("edit_employee", { ns: "employee" })}
      pageCount={1}
      bottomButtonsProps={bottomButtonsProps}
      onClose={closeSidePanel}
    >
      <EditEmployeeForm
        onSubmit={onEditEmployees}
        onClose={closeSidePanel}
        initialValues={{ email: employee.email, roles: employee.roles }}
        setBottomButtonsProps={setBottomButtonsProps}
      />
    </SideModal>
  );
};
